import { useState } from 'react';
import { Download, ChevronLeft, ChevronRight, ScrollText, Printer } from 'lucide-react';
import { useAuditLog } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const ACTION_VARIANTS: Record<
  string,
  'success' | 'info' | 'error' | 'default' | 'neutral'
> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'error',
  LOGIN: 'default',
  LOGOUT: 'neutral',
};

function exportCsv(items: any[]) {
  const header = ['Fecha', 'Usuario', 'Accion', 'Modulo', 'Entidad', 'IP'];
  const rows = items.map((a) => [
    new Date(a.createdAt).toLocaleString('es'),
    a.user?.email ?? '',
    a.action,
    a.module,
    a.metadata?.id ?? a.metadata?.event ?? '',
    a.metadata?.ip ?? '',
  ]);
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? '');
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Creó',
  UPDATE: 'Editó',
  DELETE: 'Eliminó',
  LOGIN: 'Ingresó',
  LOGOUT: 'Salió',
};

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [module, setModule] = useState('');
  const pageSize = 10;
  const { data, isLoading } = useAuditLog({
    module: module || undefined,
    page,
    pageSize,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <ScrollText size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Registro de Auditoría</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Historial de acciones realizadas en el sistema
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => exportCsv(data?.items ?? [])}>
            <Download size={14} /> Exportar CSV
          </Button>
          <Button variant="ghost" onClick={() => window.print()}>
            <Printer size={14} /> Imprimir / PDF
          </Button>
        </div>
      </div>

      <FadeIn delay={0.05}>
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
            <select className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option>Todos los usuarios</option>
            </select>
            <select
              value={module}
              onChange={(e) => {
                setModule(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Todos los módulos</option>
              <option value="auth">Autenticación</option>
              <option value="employees">Colaboradores</option>
              <option value="leave_requests">Permisos</option>
              <option value="users">Usuarios</option>
              <option value="catalogs.countries">Países</option>
              <option value="catalogs.departments">Departamentos</option>
              <option value="catalogs.positions">Puestos</option>
              <option value="catalogs.holidays">Feriados</option>
            </select>
            <input
              type="date"
              className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                  <th className="px-6 py-3">Fecha/Hora</th>
                  <th className="px-4 py-3">Usuario</th>
                  <th className="px-4 py-3">Acción</th>
                  <th className="px-4 py-3">Módulo</th>
                  <th className="px-4 py-3">Entidad</th>
                  <th className="px-4 py-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="border-b border-border-light">
                      <td className="px-6 py-4">
                        <div className="h-3 w-36 rounded brand-shimmer" />
                      </td>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-3 w-20 rounded brand-shimmer" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading &&
                  data?.items?.map((a: any) => (
                    <tr
                      key={a.id}
                      className="border-b border-border-light transition-colors hover:bg-primary-subtle/40"
                    >
                      <td className="px-6 py-4 font-mono text-xs text-font-secondary">
                        {new Date(a.createdAt).toLocaleString('es')}
                      </td>
                      <td className="px-4 py-4 text-font-primary">
                        {a.user?.email ?? '–'}
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={ACTION_VARIANTS[a.action] ?? 'neutral'} dot>
                          {ACTION_LABELS[a.action] ?? a.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-font-secondary">{a.module}</td>
                      <td className="px-4 py-4 text-font-secondary">
                        {a.metadata?.id ?? a.metadata?.event ?? '–'}
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-font-tertiary">
                        {a.metadata?.ip ?? '–'}
                      </td>
                    </tr>
                  ))}
                {!isLoading && data?.items?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
                          <ScrollText size={22} className="text-font-tertiary" />
                        </span>
                        <p className="text-sm font-medium text-font-secondary">
                          Sin registros
                        </p>
                        <p className="text-xs text-font-tertiary">
                          Ajusta los filtros para ver actividad.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-6 py-3">
            <p className="text-xs text-font-secondary">
              Mostrando {data?.items?.length ?? 0} de {total} registros
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold transition ${
                      p === page
                        ? 'bg-primary text-white shadow-sm'
                        : 'border border-border text-font-secondary hover:bg-bg'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </FadeIn>
    </PageTransition>
  );
}
